import { useSettings } from "./useSettings";

export default function SettingsScheduling() {
  const { s, setS, save } = useSettings();
  if (!s) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-8">
      <section id="set-booking" className="card-flat space-y-3 p-6" data-testid="booking-settings">
        <p className="label-caps">Owner booking &amp; availability</p>
        <p className="text-sm text-muted-foreground">
          Owners can request a visit on days that still have open slots. Every request lands in Add-ons for your approval and pricing — nothing hits the schedule until you approve it.
        </p>
        <label className="flex items-center justify-between rounded-md border border-border p-4">
          <span className="text-sm font-semibold">Enable owner booking</span>
          <button data-testid="toggle-booking-enabled" onClick={() => save({ booking_enabled: !s.booking_enabled })}
            className={`h-8 w-14 rounded-full transition-colors ${s.booking_enabled ? "bg-primary" : "bg-muted"}`}>
            <span className={`block h-6 w-6 translate-x-1 rounded-full bg-white transition-transform ${s.booking_enabled ? "translate-x-7" : ""}`} />
          </button>
        </label>
        {s.booking_enabled && (
          <>
            <label className="block text-sm font-semibold">Open slots per day
              <input data-testid="booking-capacity" type="number" min="1" className="field-input mt-1 w-full" value={s.booking_capacity ?? 4}
                onChange={(e) => setS({ ...s, booking_capacity: Number(e.target.value) })}
                onBlur={() => save({ booking_capacity: Number(s.booking_capacity ?? 4) })} /></label>
            <div>
              <p className="mb-1 text-sm font-semibold">Available days</p>
              <div className="flex gap-1">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => {
                  const cur = s.booking_weekdays || [0, 1, 2, 3, 4];
                  const on = cur.includes(i);
                  return (
                    <button key={d} data-testid={`booking-wday-${i}`}
                      onClick={() => save({ booking_weekdays: on ? cur.filter((x) => x !== i) : [...cur, i].sort() })}
                      className={`h-10 flex-1 rounded-md border text-xs font-bold ${on ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{d}</button>
                  );
                })}
              </div>
            </div>
            <label className="block text-sm font-semibold">Booking window (days ahead)
              <input data-testid="booking-window" type="number" min="1" max="60" className="field-input mt-1 w-full" value={s.booking_window_days ?? 14}
                onChange={(e) => setS({ ...s, booking_window_days: Number(e.target.value) })}
                onBlur={() => save({ booking_window_days: Number(s.booking_window_days ?? 14) })} /></label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-sm font-semibold">Express premium %
                <input data-testid="booking-express-pct" type="number" min="0" className="field-input mt-1 w-full" value={s.express_premium_pct ?? 25}
                  onChange={(e) => setS({ ...s, express_premium_pct: Number(e.target.value) })}
                  onBlur={() => save({ express_premium_pct: Number(s.express_premium_pct ?? 25) })} /></label>
              <label className="block text-sm font-semibold">Same-day rush fee $
                <input data-testid="booking-rush-fee" type="number" min="0" className="field-input mt-1 w-full" value={s.rush_flat_fee ?? 50}
                  onChange={(e) => setS({ ...s, rush_flat_fee: Number(e.target.value) })}
                  onBlur={() => save({ rush_flat_fee: Number(s.rush_flat_fee ?? 50) })} /></label>
            </div>
          </>
        )}
      </section>

      <section id="set-weather" className="card-flat space-y-3 p-6" data-testid="weather-settings">
        <p className="label-caps">Weather watches</p>
        <p className="text-sm text-muted-foreground">
          A daily forecast check for your service area (defaults to Collingwood). Storm watch emails you when snowfall passes your threshold so you can storm-blast the crew; rain watch flags wet mornings with a one-tap delay for the day's lawn jobs.
        </p>
        <div className="flex gap-2">
          <input data-testid="weather-lat" type="number" step="any" className="field-input flex-1" placeholder="Latitude" value={s.weather_lat ?? ""}
            onChange={(e) => setS({ ...s, weather_lat: Number(e.target.value) })} />
          <input data-testid="weather-lng" type="number" step="any" className="field-input flex-1" placeholder="Longitude" value={s.weather_lng ?? ""}
            onChange={(e) => setS({ ...s, weather_lng: Number(e.target.value) })} />
        </div>
        {s.offers_snow !== false && (
        <label className="flex items-center justify-between rounded-md border border-border p-4">
          <span className="text-sm font-semibold">Storm watch (snow)</span>
          <button data-testid="storm-watch-toggle" onClick={() => save({ storm_watch_enabled: !(s.storm_watch_enabled ?? true) })}
            className={`h-8 w-14 rounded-full transition-colors ${(s.storm_watch_enabled ?? true) ? "bg-primary" : "bg-muted"}`}>
            <span className={`block h-6 w-6 translate-x-1 rounded-full bg-white transition-transform ${(s.storm_watch_enabled ?? true) ? "translate-x-7" : ""}`} />
          </button>
        </label>
        )}
        {(s.offers_snow !== false && (s.storm_watch_enabled ?? true)) && (
          <div className="flex items-center gap-2 text-sm">
            <span>Alert at</span>
            <input data-testid="snowfall-threshold" type="number" className="field-input w-20" value={s.snowfall_threshold_cm ?? 5}
              onChange={(e) => setS({ ...s, snowfall_threshold_cm: Number(e.target.value) })} />
            <span>cm forecast</span>
          </div>
        )}
        {s.offers_lawn !== false && (
        <label className="flex items-center justify-between rounded-md border border-border p-4">
          <span className="text-sm font-semibold">Rain watch (lawn work)</span>
          <button data-testid="rain-watch-toggle" onClick={() => save({ rain_watch_enabled: !(s.rain_watch_enabled ?? true) })}
            className={`h-8 w-14 rounded-full transition-colors ${(s.rain_watch_enabled ?? true) ? "bg-primary" : "bg-muted"}`}>
            <span className={`block h-6 w-6 translate-x-1 rounded-full bg-white transition-transform ${(s.rain_watch_enabled ?? true) ? "translate-x-7" : ""}`} />
          </button>
        </label>
        )}
        {(s.offers_lawn !== false && (s.rain_watch_enabled ?? true)) && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span>Alert at</span>
            <input data-testid="rain-probability" type="number" className="field-input w-20" value={s.rain_probability_pct ?? 70}
              onChange={(e) => setS({ ...s, rain_probability_pct: Number(e.target.value) })} />
            <span>% chance and</span>
            <input data-testid="rain-threshold" type="number" className="field-input w-20" value={s.rain_threshold_mm ?? 5}
              onChange={(e) => setS({ ...s, rain_threshold_mm: Number(e.target.value) })} />
            <span>mm</span>
          </div>
        )}
        <button data-testid="weather-save" onClick={() => save({ weather_lat: Number(s.weather_lat), weather_lng: Number(s.weather_lng), snowfall_threshold_cm: Number(s.snowfall_threshold_cm), rain_probability_pct: Number(s.rain_probability_pct), rain_threshold_mm: Number(s.rain_threshold_mm) })}
          className="h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground">Save weather watches</button>
      </section>

      <section id="set-cadence" className="card-flat space-y-4 p-6" data-testid="cadence-settings">
        <p className="label-caps">Service cadence alerts</p>
        <p className="text-sm text-muted-foreground">
          On the Today board, an open visit turns <span className="font-bold text-[hsl(43,64%,35%)]">yellow</span> when the property's last completed service is at least this many days ago, and <span className="font-bold text-destructive">red</span> once it passes the warn threshold plus the buffer.
        </p>
        {[
          ["cadence_warn_weekly", "Weekly service — warn after (days)"],
          ["cadence_warn_biweekly", "Bi-weekly service — warn after (days)"],
          ["cadence_warn_monthly", "Monthly service — warn after (days)"],
          ["cadence_red_extra", "Turns red this many days past warn"],
        ].map(([k, label]) => (
          <div key={k} className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold">{label}</p>
            <input type="number" min="1" max="90" className="num-field" data-testid={`cadence-${k}`} value={s[k] ?? ""}
              onChange={(e) => setS({ ...s, [k]: e.target.value })}
              onBlur={() => s[k] !== "" && save({ [k]: parseInt(s[k], 10) || 0 })} />
          </div>
        ))}
      </section>
    </div>
  );
}
